/* eslint-disable max-lines */
import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import CharacterCount from '@tiptap/extension-character-count';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Image from '@tiptap/extension-image';
import Typography from '@tiptap/extension-typography';
import Mention from '@tiptap/extension-mention';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { oneDark } from '@codemirror/theme-one-dark';

import {
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Divider,
  Button,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Tooltip,
  Chip,
  Typography as MuiTypography,
  Alert,
  LinearProgress,
  Popover,
  List,
  ListItem,
  ListItemText,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  Code as CodeIcon,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Undo,
  Redo,
  FormatClear,
  TableChart,
  Image as ImageIcon,
  FormatColorText,
  FormatColorFill,
  ArrowDropDown,
  Functions,
  Link as LinkIcon,
  LinkOff,
  Visibility,
  Code,
  Lock,
  LockOpen,
  Search
} from '@mui/icons-material';
import './TemplateEditor.css';

// Variable mention suggestion component
const MentionList = ({ ref, ...props }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  const selectItem = (index) => {
    const item = props.items[index];
    if (item) {
      props.command({ id: item.name, label: item.name });
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => {
    const navigationKeys = ['ArrowUp', 'ArrowDown', 'Enter'];
    const keyHandler = (e) => {
      if (navigationKeys.includes(e.key)) {
        e.preventDefault();
        if (e.key === 'ArrowUp') upHandler();
        else if (e.key === 'ArrowDown') downHandler();
        else if (e.key === 'Enter') enterHandler();
      }
    };

    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, props.items]);

  return (
    <Paper
      ref={ref}
      elevation={3}
      sx={{
        maxHeight: 300,
        overflow: 'auto',
        minWidth: 250
      }}
    >
      {props.items.length ? (
        <List dense>
          {props.items.map((item, index) => (
            <ListItem
              key={item.name}
              button
              selected={index === selectedIndex}
              onClick={() => selectItem(index)}
              sx={{
                backgroundColor: index === selectedIndex ? 'action.selected' : 'transparent',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <ListItemText
                primaryTypographyProps={{ component: 'div' }}
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={item.type}
                      size="small"
                      color={
                        item.type === 'entity_field' ? 'primary' :
                        item.type === 'system' ? 'secondary' : 'default'
                      }
                    />
                    <Box sx={{ fontFamily: 'monospace' }}>{`{{${item.name}}}`}</Box>
                  </Box>
                }
                secondary={item.description}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
          No matching variables
        </Box>
      )}
    </Paper>
  );
};

// Enhanced toolbar component
 
 
 
 
 
 
 
 
// eslint-disable-next-line max-lines-per-function
const EnhancedToolbar = ({ editor, viewMode, setViewMode, isLocked, setSourceContent }) => {
  const [textColorAnchor, setTextColorAnchor] = useState(null);
  const [bgColorAnchor, setBgColorAnchor] = useState(null);
  const [tableAnchor, setTableAnchor] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [variableSearch, setVariableSearch] = useState('');
  const [variableAnchor, setVariableAnchor] = useState(null);

  if (!editor) return null;

  const colors = [
    '#000000', '#434343', '#666666', '#999999', '#cccccc', '#ffffff',
    '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#0000ff',
    '#9900ff', '#ff00ff', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3',
    '#d0e0e3', '#cfe2f3', '#d9d2e9', '#ead1dc', '#ea9999', '#f9cb9c',
    '#ffe599', '#b6d7a8', '#a2c4c9', '#9fc5e8', '#b4a7d6', '#d5a6bd'
  ];

  const handleTextColor = (color) => {
    editor.chain().focus().setColor(color).run();
    setTextColorAnchor(null);
  };

  const handleBgColor = (color) => {
    editor.chain().focus().toggleHighlight({ color }).run();
    setBgColorAnchor(null);
  };

  const addTable = (rows, cols) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    setTableAnchor(null);
  };

  const setLink = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
    }
    setLinkDialogOpen(false);
    setLinkUrl('');
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
  };

  return (
    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      p: 1,
      borderBottom: 1,
      borderColor: 'divider',
      alignItems: 'center'
    }}>
      {/* View Mode Toggle */}
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(e, v) => {
          if (v && v !== viewMode) {
            // Format HTML only when switching TO source mode
            if (v === 'source' && editor) {
              const html = editor.getHTML();
              setSourceContent(formatHtml(html));
            }
            setViewMode(v);
          }
        }}
        size="small"
      >
        <ToggleButton value="wysiwyg">
          <Visibility sx={{ mr: 0.5 }} fontSize="small" /> Visual
        </ToggleButton>
        <ToggleButton value="source">
          <Code sx={{ mr: 0.5 }} fontSize="small" /> Source
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Lock Status */}
      {isLocked !== undefined && (
        <>
          <Chip
            icon={isLocked ? <Lock /> : <LockOpen />}
            label={isLocked ? "View Only" : "Editing"}
            color={isLocked ? "warning" : "success"}
            size="small"
          />
          <Divider orientation="vertical" flexItem />
        </>
      )}

      {/* Only show editing tools in WYSIWYG mode and when not locked */}
      {viewMode === 'wysiwyg' && !isLocked && (
        <>
          {/* Text style selector */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={
                editor.isActive('heading', { level: 1 }) ? 'h1' :
                editor.isActive('heading', { level: 2 }) ? 'h2' :
                editor.isActive('heading', { level: 3 }) ? 'h3' :
                editor.isActive('heading', { level: 4 }) ? 'h4' :
                editor.isActive('heading', { level: 5 }) ? 'h5' :
                editor.isActive('heading', { level: 6 }) ? 'h6' :
                'paragraph'
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'paragraph') {
                  editor.chain().focus().setParagraph().run();
                } else {
                  const level = parseInt(value.replace('h', ''));
                  editor.chain().focus().toggleHeading({ level }).run();
                }
              }}
            >
              <MenuItem value="paragraph">Normal</MenuItem>
              <MenuItem value="h1">Heading 1</MenuItem>
              <MenuItem value="h2">Heading 2</MenuItem>
              <MenuItem value="h3">Heading 3</MenuItem>
              <MenuItem value="h4">Heading 4</MenuItem>
              <MenuItem value="h5">Heading 5</MenuItem>
              <MenuItem value="h6">Heading 6</MenuItem>
            </Select>
          </FormControl>

          <Divider orientation="vertical" flexItem />

          {/* Text formatting */}
          <ToggleButtonGroup size="small">
            <ToggleButton
              value="bold"
              selected={editor.isActive('bold')}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <FormatBold fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="italic"
              selected={editor.isActive('italic')}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <FormatItalic fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="underline"
              selected={editor.isActive('underline')}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
              <FormatUnderlined fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="strike"
              selected={editor.isActive('strike')}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            >
              <FormatStrikethrough fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="code"
              selected={editor.isActive('code')}
              onClick={() => editor.chain().focus().toggleCode().run()}
            >
              <CodeIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem />

          {/* Colors */}
          <Tooltip title="Text Color">
            <IconButton size="small" onClick={(e) => setTextColorAnchor(e.currentTarget)}>
              <FormatColorText fontSize="small" />
              <ArrowDropDown fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={textColorAnchor}
            open={Boolean(textColorAnchor)}
            onClose={() => setTextColorAnchor(null)}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5, p: 1 }}>
              {colors.map(color => (
                <Box
                  key={color}
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    '&:hover': { opacity: 0.8, transform: 'scale(1.1)' }
                  }}
                  onClick={() => handleTextColor(color)}
                />
              ))}
            </Box>
          </Menu>

          <Tooltip title="Background Color">
            <IconButton size="small" onClick={(e) => setBgColorAnchor(e.currentTarget)}>
              <FormatColorFill fontSize="small" />
              <ArrowDropDown fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={bgColorAnchor}
            open={Boolean(bgColorAnchor)}
            onClose={() => setBgColorAnchor(null)}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5, p: 1 }}>
              {colors.map(color => (
                <Box
                  key={color}
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    '&:hover': { opacity: 0.8, transform: 'scale(1.1)' }
                  }}
                  onClick={() => handleBgColor(color)}
                />
              ))}
            </Box>
          </Menu>

          <Divider orientation="vertical" flexItem />

          {/* Lists and alignment */}
          <ToggleButtonGroup size="small">
            <ToggleButton
              value="bulletList"
              selected={editor.isActive('bulletList')}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <FormatListBulleted fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="orderedList"
              selected={editor.isActive('orderedList')}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <FormatListNumbered fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="blockquote"
              selected={editor.isActive('blockquote')}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
            >
              <FormatQuote fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem />

          {/* Alignment */}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={
              editor.isActive({ textAlign: 'left' }) ? 'left' :
              editor.isActive({ textAlign: 'center' }) ? 'center' :
              editor.isActive({ textAlign: 'right' }) ? 'right' :
              editor.isActive({ textAlign: 'justify' }) ? 'justify' :
              'left'
            }
          >
            <ToggleButton
              value="left"
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
            >
              <FormatAlignLeft fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="center"
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
            >
              <FormatAlignCenter fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="right"
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
            >
              <FormatAlignRight fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="justify"
              onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            >
              <FormatAlignJustify fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem />

          {/* Insert items */}
          <Tooltip title="Insert Table">
            <IconButton size="small" onClick={(e) => setTableAnchor(e.currentTarget)}>
              <TableChart fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={tableAnchor}
            open={Boolean(tableAnchor)}
            onClose={() => setTableAnchor(null)}
          >
            <Box sx={{ p: 2 }}>
              <MuiTypography variant="body2" gutterBottom>Select table size</MuiTypography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5 }}>
                {[...Array(8)].map((_, row) => (
                  [...Array(8)].map((_, col) => (
                    <Box
                      key={`${row}-${col}`}
                      sx={{
                        width: 24,
                        height: 24,
                        border: '1px solid #ccc',
                        cursor: 'pointer',
                        backgroundColor: 'background.paper',
                        '&:hover': { backgroundColor: 'primary.light' }
                      }}
                      onClick={() => addTable(row + 1, col + 1)}
                    />
                  ))
                ))}
              </Box>
            </Box>
          </Menu>

          <Tooltip title="Insert/Edit Link">
            <IconButton
              size="small"
              onClick={() => setLinkDialogOpen(true)}
              color={editor.isActive('link') ? 'primary' : 'default'}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {editor.isActive('link') && (
            <Tooltip title="Remove Link">
              <IconButton size="small" onClick={removeLink}>
                <LinkOff fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Insert Image">
            <IconButton size="small" onClick={() => {
              const url = window.prompt('Enter image URL:');
              if (url) {
                editor.chain().focus().setImage({ src: url }).run();
              }
            }}>
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Insert Variable">
            <IconButton
              size="small"
              onClick={(e) => setVariableAnchor(e.currentTarget)}
              color="primary"
            >
              <Functions fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          {/* History */}
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          >
            <FormatClear fontSize="small" />
          </IconButton>

          {/* Table controls when in a table */}
          {editor.isActive('table') && (
            <>
              <Divider orientation="vertical" flexItem />
              <Button size="small" onClick={() => editor.chain().focus().addColumnBefore().run()}>
                Add Col Before
              </Button>
              <Button size="small" onClick={() => editor.chain().focus().addColumnAfter().run()}>
                Add Col After
              </Button>
              <Button size="small" onClick={() => editor.chain().focus().deleteColumn().run()}>
                Delete Col
              </Button>
              <Button size="small" onClick={() => editor.chain().focus().addRowBefore().run()}>
                Add Row Before
              </Button>
              <Button size="small" onClick={() => editor.chain().focus().addRowAfter().run()}>
                Add Row After
              </Button>
              <Button size="small" onClick={() => editor.chain().focus().deleteRow().run()}>
                Delete Row
              </Button>
            </>
          )}
        </>
      )}

      {/* Link Dialog */}
      <Popover
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <TextField
            fullWidth
            label="URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setLink();
              }
            }}
            placeholder="https://example.com"
            size="small"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setLinkDialogOpen(false)}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={setLink}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Variable Search Popover */}
      <Popover
        open={Boolean(variableAnchor)}
        anchorEl={variableAnchor}
        onClose={() => setVariableAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search variables..."
            value={variableSearch}
            onChange={(e) => setVariableSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <MuiTypography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Type @ in the editor to insert variables inline
          </MuiTypography>
        </Box>
      </Popover>
    </Box>
  );
};

// Status bar component
const StatusBar = ({ editor }) => {
  if (!editor) return null;

  const characterCount = editor.storage.characterCount?.characters() || 0;
  const wordCount = editor.storage.characterCount?.words() || 0;

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      px: 2,
      py: 1,
      borderTop: 1,
      borderColor: 'divider',
      backgroundColor: 'background.paper',
      fontSize: '0.875rem',
      color: 'text.secondary'
    }}>
      <Box>
        {wordCount} words • {characterCount} characters
      </Box>
      <Box>
        {editor.isActive('bold') && <Chip label="Bold" size="small" sx={{ mx: 0.5 }} />}
        {editor.isActive('italic') && <Chip label="Italic" size="small" sx={{ mx: 0.5 }} />}
        {editor.isActive('link') && <Chip label="Link" size="small" sx={{ mx: 0.5 }} />}
      </Box>
    </Box>
  );
};

// Main Enhanced Template Editor Component
// Helper function to format HTML with proper indentation
const formatHtml = (html) => {
  // Basic HTML formatting - add newlines after tags
  let formatted = html
    .replace(/></g, '>\n<')  // Add newline between tags
    .replace(/(<\/(p|div|h[1-6]|ul|ol|li|table|tr|td|th)>)/gi, '$1\n') // Add newline after block elements
    .replace(/(<(p|div|h[1-6]|ul|ol|li|table|tr|td|th)[^>]*>)/gi, '\n$1') // Add newline before block elements
    .replace(/(\n\s*\n)+/g, '\n') // Remove multiple blank lines
    .trim();

  // Simple indentation
  const lines = formatted.split('\n');
  let indentLevel = 0;
  const indentSize = 2;

  return lines.map(line => {
    const trimmedLine = line.trim();

    // Decrease indent for closing tags
    if (trimmedLine.startsWith('</')) {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    const indentedLine = ' '.repeat(indentLevel * indentSize) + trimmedLine;

    // Increase indent for opening tags (but not self-closing)
    if (trimmedLine.startsWith('<') && !trimmedLine.startsWith('</') && !trimmedLine.endsWith('/>') && !trimmedLine.includes('</')) {
      indentLevel++;
    }

    return indentedLine;
  }).join('\n');
};

// eslint-disable-next-line max-lines-per-function
const EnhancedTemplateEditor = ({
  initialContent = '',
  onChange,
  variables = [],
  isLocked = false,
  onLockToggle,
  lockStatus,
  autoSave = true,
  onAutoSave
}) => {
  const [viewMode, setViewMode] = useState('wysiwyg');
  const [sourceContent, setSourceContent] = useState(initialContent);
  const [isSaving, setIsSaving] = useState(false);
  const autoSaveTimeoutRef = useRef(null);
  const viewModeRef = useRef(viewMode);

  // Keep viewModeRef in sync
  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  // Variable mention suggestion configuration
  const variableSuggestion = {
    items: ({ query }) => {
      return variables
        .filter(item => item.name.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 10);
    },
    render: () => {
      let component;
      let popup;

      return {
        onStart: props => {
          component = new MentionList(props);

          if (!props.clientRect) {
            return;
          }

          popup = document.createElement('div');
          popup.style.position = 'absolute';
          popup.style.zIndex = '9999';
          popup.appendChild(component.element);
          document.body.appendChild(popup);
          const rect = props.clientRect();
          if (rect) {
            popup.style.left = `${rect.left}px`;
            popup.style.top = `${rect.bottom}px`;
          }
        },
        onUpdate(props) {
          component.updateProps(props);

          if (!props.clientRect) {
            return;
          }

          popup.style.left = `${props.clientRect().left}px`;
          popup.style.top = `${props.clientRect().bottom}px`;
        },
        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            popup.style.display = 'none';
            return true;
          }
          return component.onKeyDown(props);
        },
        onExit() {
          popup.remove();
          component.destroy();
        },
      };
    },
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        history: {
          depth: 100,
        },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      CharacterCount.configure({
        limit: 100000,
      }),
      Placeholder.configure({
        placeholder: 'Start typing your template... Use {{variableName}} or type @ to insert variables.',
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      Color,
      TextStyle,
      Image.configure({
        allowBase64: true,
        HTMLAttributes: {
          class: 'template-image',
        },
      }),
      Typography,
      Mention.configure({
        HTMLAttributes: {
          class: 'template-variable',
        },
        suggestion: variableSuggestion,
        renderLabel({ _options, node }) {
          return `{{${node.attrs.label ?? node.attrs.id}}}`;
        },
      }),
    ],
    content: initialContent,
    editable: !isLocked,
    editorProps: {
      attributes: {
        class: 'tiptap-editor'
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();

      // Only update source content when in WYSIWYG mode
      if (viewModeRef.current === 'wysiwyg') {
        setSourceContent(html);
        onChange?.(html);
      }

      // Auto-save functionality
      if (autoSave && onAutoSave && viewModeRef.current === 'wysiwyg') {
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }

        setIsSaving(true);
        autoSaveTimeoutRef.current = setTimeout(() => {
          onAutoSave(html).then(() => {
            setIsSaving(false);
          });
        }, 2000); // Save after 2 seconds of no changes
      }
    },
  });

  // Update editor content when initialContent changes
  useEffect(() => {
    if (editor && initialContent !== undefined && initialContent !== editor.getHTML()) {
      editor.commands.setContent(initialContent);
      setSourceContent(initialContent);
    }
  }, [initialContent, editor]);

  // Update editor content when switching to WYSIWYG mode
  useEffect(() => {
    if (editor && viewMode === 'wysiwyg') {
      // When switching to visual mode, update TipTap with the current source content
      editor.commands.setContent(sourceContent);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]); // Only run when viewMode changes

  // Update editable state when lock status changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!isLocked);
    }
  }, [isLocked, editor]);

  // Sync source changes back to editor
  const handleSourceChange = (value) => {
    setSourceContent(value);
    // Only notify parent when in source mode
    if (viewMode === 'source') {
      onChange?.(value);
    }
  };

  // Clean up auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Paper elevation={0} sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 1
    }}>
      {/* Lock status banner */}
      {lockStatus && (
        <Alert
          severity={lockStatus.isLockedByMe ? "info" : "warning"}
          icon={<Lock />}
          action={
            lockStatus.isLockedByMe && onLockToggle && (
              <Button size="small" onClick={onLockToggle}>
                Release Lock
              </Button>
            )
          }
        >
          {lockStatus.isLockedByMe
            ? "You have locked this template for editing"
            : `This template is locked by ${lockStatus.lockedByEmail}`
          }
        </Alert>
      )}

      {/* Auto-save indicator */}
      {isSaving && (
        <LinearProgress sx={{ height: 2 }} />
      )}

      <EnhancedToolbar
        editor={editor}
        viewMode={viewMode}
        setViewMode={setViewMode}
        isLocked={isLocked}
        sourceContent={sourceContent}
        setSourceContent={setSourceContent}
      />

      <Box sx={{ position: 'relative', minHeight: 400 }}>
        {viewMode === 'wysiwyg' ? (
          <Box sx={{
            p: 2,
            '& .tiptap-editor': {
              outline: 'none',
              minHeight: 400,
              '& .template-variable': {
                backgroundColor: 'primary.light',
                color: 'primary.main',
                padding: '2px 6px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.95em',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  cursor: 'pointer',
                }
              },
              '& .ProseMirror-placeholder': {
                color: 'text.disabled',
                opacity: 0.7,
              },
            }
          }}>
            <EditorContent editor={editor} />
          </Box>
        ) : (
          <CodeMirror
            value={sourceContent}
            height="400px"
            extensions={[html()]}
            onChange={handleSourceChange}
            editable={!isLocked}
            theme={oneDark}
          />
        )}
      </Box>

      <StatusBar editor={editor} />
    </Paper>
  );
};

export default EnhancedTemplateEditor;